import {
    Component,
    ViewEncapsulation,
    inject,
    effect,
    ChangeDetectionStrategy,
} from '@angular/core';

import { Router, RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { TranslocoModule } from '@jsverse/transloco';
import { trigger, transition, style, animate } from '@angular/animations';
import { AccountDrawerService } from '../../../core/services/account-drawer.service';
import { UserService } from '../../../core/user/user.service';
import { PermissionsService } from '../../../core/services/permissions.service';
import { ErrorService } from '../../../core/services/error.service';
import { environment } from '../../../../environments/environment';
import { ThemeColorService } from '../../../core/services/theme-color.service';

@Component({
    selector: 'ph-account-drawer',
    standalone: true,
    imports: [RouterModule, MatIconModule, MatButtonModule, TranslocoModule],
    templateUrl: './account-drawer.component.html',
    styleUrl: './account-drawer.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('drawerSlide', [
            transition(':enter', [
                style({ transform: 'translateX(100%)' }),
                animate('280ms cubic-bezier(0.4,0,0.2,1)', style({ transform: 'translateX(0)' })),
            ]),
            transition(':leave', [
                animate('240ms cubic-bezier(0.4,0,0.2,1)', style({ transform: 'translateX(100%)' })),
            ]),
        ]),
        trigger('scrimFade', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('200ms ease', style({ opacity: 1 })),
            ]),
            transition(':leave', [
                animate('200ms ease', style({ opacity: 0 })),
            ]),
        ]),
    ],
})
export class AccountDrawerComponent {
    readonly drawerService = inject(AccountDrawerService);
    private _userService = inject(UserService);
    private _permissions = inject(PermissionsService);
    private _errorService = inject(ErrorService);
    private _router = inject(Router);
    private _themeColor = inject(ThemeColorService);

    constructor() {
        effect(() => {
            this.drawerService.isOpen()
                ? this._themeColor.set(this._themeColor.GRADIENT)
                : this._themeColor.reset();
        });
    }

    readonly isProduction = environment.releaseStage === 'production';
    readonly appVersion: string = environment.version;

    get user() { return this._userService.user; }
    get isManagerOrAdmin() { return this._permissions.hasRole(['admin', 'captain']); }

    close(): void {
        this.drawerService.close();
    }

    navigate(path: string): void {
        this.drawerService.closeInstant();
        this._router.navigate([path]);
    }

    signOut(): void {
        this.close();
        this._router.navigate(['/sign-out']);
    }

    testBugsnag(): void {
        this._errorService.reportError(new Error('Test Error'), [
            { key: 'test_info', data: { basic_data: 'Hello', extra_data: 'World' } },
        ]);
    }

    testErrorHandler(): void {
        throw new Error('Test Unhandled Error');
    }
}
